<script setup>
// 格式 Hub 首页 /format/（nuxt#8，复刻 design 的 Format Hub.html，替换旧表格版）。搜索/筛选用 Vue 响应式。
import { ref, computed } from "vue";
import { FORMATS, FORMAT_TYPES } from "@/settings/free-tools/formats-data";
import "@/assets/styles/free-tools.css";

definePageMeta({ layout: "free-tools" });

const TYPE_COLOR = { raster: "#ff6f9c", vector: "#16a39c", raw: "#e0a32e", animated: "#34d399", icon: "#5b8def", print: "#a78bfa", video: "#4ea2ff", audio: "#22d3ee" };
const TYPE_IC = {
  raster: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.6"/><path d="M21 15l-5-5L5 21"/></svg>`,
  vector: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="16" width="5" height="5" rx="1"/><path d="M8 5.5h6a4 4 0 014 4v6"/></svg>`,
  raw: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><circle cx="12" cy="12.5" r="3.2"/></svg>`,
  animated: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="14" rx="3"/><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none"/></svg>`,
  icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="4" y="4" width="16" height="16" rx="4"/><circle cx="12" cy="11" r="2.5"/><path d="M7 17c1.2-2 8.8-2 10 0"/></svg>`,
  print: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="6" y="9" width="12" height="7" rx="1"/><path d="M6 9V4h12v5M8 16v4h8v-4"/></svg>`,
  video: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="14" rx="3"/><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none"/></svg>`,
  audio: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/></svg>`,
};
const fslug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

const search = ref("");
const activeFilter = ref("all");

const popular = computed(() => FORMATS.filter((f) => f[8]).slice(0, 8).map((f) => ({ ext: f[0], full: f[2], type: f[7], cc: TYPE_COLOR[f[7]], slug: fslug(f[0]) })));
const cats = computed(() => {
  const q = search.value.trim().toLowerCase();
  return FORMAT_TYPES
    .filter((t) => activeFilter.value === "all" || activeFilter.value === t[0])
    .map((t) => {
      const items = FORMATS.filter((f) => f[7] === t[0]).filter((f) => !q || f[0].toLowerCase().includes(q) || f[2].toLowerCase().includes(q));
      return { key: t[0], label: t[1], sub: t[2], cc: TYPE_COLOR[t[0]], ic: TYPE_IC[t[0]], items: items.map((f) => ({ ext: f[0], full: f[2], slug: fslug(f[0]) })) };
    })
    .filter((c) => c.items.length);
});
const showPopular = computed(() => !search.value.trim() && activeFilter.value === "all");
const noRes = computed(() => !cats.value.length);

useHead({
  title: "Image & Media Format Guide — Open, Convert & Edit Any Format | SharkFoto",
  meta: [{ name: "description", content: "Every image, video and audio format explained — what it is, how to open it, and free tools to convert, compress and edit it." }],
  link: [{ rel: "canonical", href: "https://sharkfoto.com/format/" }],
  script: [{ type: "application/ld+json", innerHTML: JSON.stringify({ "@context": "https://schema.org", "@type": "CollectionPage", name: "Image & Media Format Guide", description: "Every image, video and audio format explained, with free tools to convert, compress and edit.", url: "https://sharkfoto.com/format/" }) }],
});
</script>

<template>
  <div class="ft-scope">
    <section class="fh-hero">
      <div class="wrap in">
        <h1>Every format,<br /><span class="hl">explained &amp; ready to convert</span></h1>
        <p>What each image, video and audio format is, how to open it, and the free tools to convert, compress or edit it — plus AI when you need more.</p>
        <div class="fh-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input v-model="search" type="text" placeholder="Search a format — e.g. WEBP, HEIC, RAW…" />
        </div>
        <div class="fh-filters">
          <button class="fh-filter" :class="{ active: activeFilter === 'all' }" @click="activeFilter = 'all'">All</button>
          <button v-for="t in FORMAT_TYPES" :key="t[0]" class="fh-filter" :class="{ active: activeFilter === t[0] }" @click="activeFilter = t[0]">{{ t[1] }}</button>
        </div>
      </div>
    </section>

    <div class="wrap">
      <section v-show="showPopular">
        <div class="sec-h" style="text-align: left; margin-bottom: 16px"><h2 style="font-size: 22px">Popular formats</h2></div>
        <div class="pop-grid">
          <NuxtLink v-for="p in popular" :key="p.ext" class="pop-card" :style="{ '--cc': p.cc }" :to="'/format/' + p.slug + '/'">
            <div class="ext">.{{ p.ext }}</div>
            <div class="nm">{{ p.full }}</div>
            <span class="tag">{{ p.type }}</span>
          </NuxtLink>
        </div>
      </section>

      <div>
        <section v-for="c in cats" :key="c.key" class="fmt-cat" :style="{ '--cc': c.cc }">
          <div class="fmt-cat-head"><span class="fmt-cat-badge" v-html="c.ic"></span><h2>{{ c.label }}</h2><span class="ct">{{ c.items.length }} formats</span></div>
          <p class="fmt-cat-sub">{{ c.sub }}</p>
          <div class="fmt-list">
            <NuxtLink v-for="f in c.items" :key="f.ext" class="fmt-row" :to="'/format/' + f.slug + '/'">
              <span class="fx">.{{ f.ext }}</span><span class="fn">{{ f.full }}</span>
            </NuxtLink>
          </div>
        </section>
      </div>
      <div v-if="noRes" class="fh-nores">No formats match your search.</div>
    </div>

    <section class="block">
      <div class="wrap">
        <div class="upsell" style="background: linear-gradient(180deg, var(--surface), var(--bg-2))">
          <div class="upsell-in">
            <h2>Got the format — now do something with it</h2>
            <p>Convert, compress and edit any format with free tools, or bring in AI to remove backgrounds, upscale and generate.</p>
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap">
              <NuxtLink class="btn btn-primary btn-lg" to="/free-tools">Browse free tools</NuxtLink>
              <NuxtLink class="btn btn-outline btn-lg" to="/ai/effects/">Explore AI</NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.fh-hero { text-align: center; padding: 54px 0 34px; position: relative; overflow: hidden; }
.fh-hero::before { content: ""; position: absolute; inset: 0; background: radial-gradient(70% 90% at 50% -10%, rgba(78, 162, 255, 0.14), transparent 60%); pointer-events: none; }
.fh-hero .in { position: relative; z-index: 1; }
.fh-hero h1 { font-size: clamp(32px, 5vw, 56px); font-weight: 700; }
.fh-hero h1 .hl { background: linear-gradient(110deg, #4ea2ff, #8b5cf6); -webkit-background-clip: text; background-clip: text; color: transparent; }
.fh-hero p { font-size: clamp(16px, 2vw, 19px); color: var(--tx-dim); max-width: 600px; margin: 18px auto 0; }
.fh-search { display: flex; align-items: center; gap: 10px; max-width: 520px; margin: 28px auto 0; background: var(--surface); border: 1px solid var(--line-strong); border-radius: 14px; padding: 5px 6px 5px 18px; }
.fh-search svg { width: 20px; height: 20px; color: var(--tx-faint); flex: none; }
.fh-search input { flex: 1; background: none; border: none; outline: none; color: var(--tx); font-family: var(--font-b); font-size: 16px; font-weight: 500; padding: 11px 0; }
.fh-filters { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; margin: 22px auto 0; }
.fh-filter { padding: 9px 16px; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); font-family: var(--font-b); font-size: 14px; font-weight: 700; color: var(--tx-dim); cursor: pointer; transition: 0.15s; }
.fh-filter:hover { color: var(--tx); }
.fh-filter.active { background: color-mix(in oklab, #4ea2ff 16%, transparent); border-color: #4ea2ff; color: #a9d2ff; }
.pop-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-top: 14px; }
.pop-card { position: relative; border: 1px solid var(--line); border-radius: 16px; background: var(--surface); padding: 20px; transition: 0.18s; display: block; }
.pop-card:hover { border-color: var(--cc); background: var(--surface-2); transform: translateY(-3px); }
.pop-card .ext { font-family: var(--font-h); font-weight: 700; font-size: 24px; color: var(--cc); }
.pop-card .nm { font-size: 13px; color: var(--tx-dim); font-weight: 600; margin: 4px 0 10px; }
.pop-card .tag { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 6px; padding: 3px 8px; }
.fmt-cat { margin-top: 48px; }
.fmt-cat-head { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.fmt-cat-badge { width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; color: #fff; flex: none; background: var(--cc); }
.fmt-cat-badge :deep(svg) { width: 22px; height: 22px; }
.fmt-cat-head h2 { font-size: 23px; }
.fmt-cat-head .ct { font-family: var(--font-h); font-size: 13px; font-weight: 600; color: var(--tx-faint); }
.fmt-cat-sub { color: var(--tx-dim); font-size: 14px; margin: 0 0 18px 52px; }
.fmt-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
.fmt-row { display: flex; align-items: center; gap: 12px; padding: 13px 15px; border: 1px solid var(--line); border-radius: 12px; background: var(--surface); transition: 0.15s; }
.fmt-row:hover { border-color: var(--cc); background: var(--surface-2); transform: translateY(-2px); }
.fmt-row .fx { font-family: var(--font-h); font-weight: 700; font-size: 15px; color: var(--cc); min-width: 48px; }
.fmt-row .fn { font-size: 12.5px; color: var(--tx-dim); font-weight: 600; line-height: 1.25; }
.fh-nores { text-align: center; color: var(--tx-faint); font-weight: 600; padding: 50px 0; }
@media (max-width: 900px) { .pop-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 620px) { .pop-grid { grid-template-columns: repeat(2, 1fr); } .fmt-cat-sub { margin-left: 0; } }
</style>
